// Assign agent section for SR detail page — replaces AssignCarerSection for
// non-care service types. Fetches agents by type and assigns via POST /sr/:id/assign-agent.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";

interface AgentItem { _id: string; name: string; phone: string; available: boolean }
interface AgentListResponse { data: AgentItem[]; total: number; page: number; limit: number }

const SERVICE_TO_AGENT: Record<string, { type: string; label: string }> = {
  medicine_delivery:    { type: "delivery_agent",           label: "Assign Delivery Agent" },
  diagnostics:          { type: "sample_collection_agent",  label: "Assign Sample Collection Agent" },
  nutritionist:         { type: "nutritionist_agent",       label: "Assign Nutritionist" },
  property_management:  { type: "property_manager",         label: "Assign Property Manager" },
};

interface Props {
  serviceType: string;
  srId: string;
  currentAgentId: string | null;
  currentAgentType: string | null;
  onAssigned: () => void;
}

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
export function AssignAgentSection({ serviceType, srId, currentAgentId, onAssigned }: Props): JSX.Element | null {
  const mapping = SERVICE_TO_AGENT[serviceType] as { type: string; label: string } | undefined;
  const agentType = mapping?.type ?? "";
  const agentLabel = mapping?.label ?? "";

  const [selected, setSelected] = useState(currentAgentId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data, isLoading, isError } = useQuery<AgentListResponse>({
    queryKey: ["agents", agentType],
    queryFn: () => apiFetch<AgentListResponse>(`/agents?type=${agentType}`),
    enabled: Boolean(mapping),
  });

  // State 7: wrong service type → render nothing
  if (!mapping) return null;

  const available = data?.data.filter((a) => a.available) ?? [];
  const currentAgent = data?.data.find((a) => a._id === currentAgentId);

  async function handleAssign(): Promise<void> {
    setSaving(true); setError(null); setSuccess(false);
    try {
      await apiFetch(`/sr/${srId}/assign-agent`, {
        method: "POST",
        body: JSON.stringify({ agent_id: selected, agent_type: agentType }),
      });
      setSuccess(true);
      onAssigned();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Assignment failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <h2 className="text-sm font-semibold text-slate-700">{agentLabel}</h2>

      {/* State 4: agent assigned — show current */}
      {currentAgent && (
        <p className="text-sm text-navy-800">
          Assigned: <span className="font-medium">{currentAgent.name}</span> · {currentAgent.phone}
        </p>
      )}

      {/* State 1: loading */}
      {isLoading && <p className="text-sm text-slate-400">Loading…</p>}

      {/* State 2: no agents */}
      {!isLoading && !isError && available.length === 0 && (
        <p className="text-sm text-slate-400">
          No {agentLabel.replace("Assign ", "").toLowerCase()}s added yet. Add agents in Agents settings.
        </p>
      )}

      {/* State 3 & 4: dropdown */}
      {!isLoading && !isError && available.length > 0 && (
        <>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
          >
            <option value="">— select —</option>
            {available.map((a) => (
              <option key={a._id} value={a._id}>{a.name} · {a.phone}</option>
            ))}
          </select>
          {/* State 5: assigning */}
          <button
            disabled={!selected || saving}
            onClick={() => void handleAssign()}
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 disabled:opacity-40 transition-colors"
          >
            {saving ? "Assigning…" : "Assign"}
          </button>
        </>
      )}

      {/* State 6: error */}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {/* Success toast */}
      {success && <p className="text-sm text-navy-700">Agent assigned successfully.</p>}
    </div>
  );
}
